import React from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Link as MuiLink, Typography } from '@mui/material';
import { TextField } from '../../../components/TextField';
import { errorTranslationKey, ErrorType } from '../../../common/error';
import { LoadingButton } from '@lialo/pwa-ui/lib/LoadingButton';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { DiscountCodeInfo } from '../../../common/models/code-info';
import { useValidateDiscountCoupon } from './useValidateDiscountCoupon';
import { Link } from 'react-router-dom';

const PREFIX = 'RedeemCouponDialog';

const classes = {
  paper: `${PREFIX}-paper`,
  content: `${PREFIX}-content`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.paper}`]: {
    width: '100%',
  },

  [`& .${classes.content}`]: {
    paddingTop: '0 !important',
  },
}));

interface Props {
  onClose: (discountCodeInfo?: DiscountCodeInfo) => void;
  shortLink: string;
}

export const RedeemCouponDialog = ({ onClose, shortLink }: Props) => {
  const { t } = useTranslation();
  const codeFieldRef = React.useRef<HTMLInputElement | null>(null);
  const { execute, error, loading } = useValidateDiscountCoupon(onClose);

  const onSubmit = () => {
    execute({ code: codeFieldRef.current!!.value.trim(), shortLink });
  };

  return (
    <StyledDialog classes={{ paper: classes.paper }} open={true}>
      <DialogTitle>{t('checkout.coupon.dialog.title')}</DialogTitle>
      <DialogContent className={classes.content}>
        <TextField
          autoFocus={true}
          ref={codeFieldRef}
          error={!!error}
          helperText={error ? t(errorTranslationKey(error)) : undefined}
          label={t('checkout.coupon.dialog.code_label')}
        />
        {error?.errorCode === ErrorType.UNEXPECTED_CODE_TYPE && (
          <Typography variant={'body2'}>
            {t('checkout.coupon.dialog.start_code_1')}
            <MuiLink component={Link} to={'/coupon'}>
              {t('checkout.coupon.dialog.start_code_2')}
            </MuiLink>
            {t('checkout.coupon.dialog.start_code_3')}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button color={'primary'} onClick={() => onClose()}>
          {t('common.cancel')}
        </Button>
        <LoadingButton isLoading={loading} component={Button} color="primary" onClick={onSubmit}>
          {t('checkout.coupon.dialog.redeem')}
        </LoadingButton>
      </DialogActions>
    </StyledDialog>
  );
};
